/**
 * FS-686: catalog rows the nightly sweep generates from a certified base row
 * by face-area scaling. They are rebuilt from the base every night, so a hand
 * edit would be overwritten the next morning, and deleting one would clear
 * the devices matched to it. device-data-api refuses PUT, rename, history
 * edits and DELETE on them with 409 `derived_row`; the console disables the
 * controls up front so the operator is sent to the base row instead.
 */
export const DERIVED_SOURCES: readonly string[] = ['certified_scaled', 'extrapolated'];

export const isDerivedSource = (source: string | null | undefined): boolean =>
  source != null && DERIVED_SOURCES.includes(source);

/** The base row's id from source_meta, or null when the meta does not name one. */
export const derivedBaseId = (meta: Record<string, unknown> | null | undefined): number | null => {
  const v = meta?.base_filter_id;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && /^\d+$/.test(v.trim())) return Number(v.trim());
  return null;
};

/** The short note shown beside the disabled edit and delete controls. */
export const derivedRowNote = (meta: Record<string, unknown> | null | undefined): string => {
  const base = derivedBaseId(meta);
  return base != null
    ? `Derived row, regenerated nightly from entry #${base}; edit the base row`
    : 'Derived row, regenerated nightly from its base entry; edit the base row';
};

/**
 * The API's 409 for a derived row, in words an operator can act on. Returns
 * null for any other error so the caller falls back to its usual message.
 */
export const derivedRowErrorMessage = (
  err: unknown,
  meta: Record<string, unknown> | null | undefined,
): string | null => {
  const res = (err as { response?: { data?: { error?: unknown } } } | null)?.response;
  if (res?.data?.error === 'derived_row') return `${derivedRowNote(meta)}.`;
  return null;
};
